import { useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { buildPaletteFromPrimary } from '../../charts/shared/palettes';
import {
  EXTERNAL_PRIMARY_COLORS,
  EXTERNAL_BACKGROUND_COLORS,
} from '../../config/deploymentProfile';
import { deriveThemeFromBackground, deriveUiFromBackground } from '../../utils/accessibility';

export function LockedColorModeControl({ config, onChange, ui }) {
  const current = config.lockedColorMode || 'multi';
  return (
    <div>
      <label className="block text-xs mb-2" style={{ color: ui.textSecondary }}>Color Mode</label>
      <div className="flex gap-1">
        {['single', 'multi'].map((mode) => (
          <button
            key={mode}
            onClick={() => onChange('lockedColorMode', mode)}
            className="flex-1 text-xs py-1 px-2 border rounded capitalize"
            style={{
              backgroundColor: current === mode ? ui.accent : ui.bgSecondary,
              color: current === mode ? ui.accentFg : ui.textSecondary,
              borderColor: current === mode ? ui.accent : ui.border,
            }}
          >
            {mode === 'single' ? 'Single' : 'Multi'}
          </button>
        ))}
      </div>
    </div>
  );
}

export function ExternalColorControl({ control, config, onChange, ui }) {
  const [hexInput, setHexInput] = useState('');
  const presetColors = control.presetColors || EXTERNAL_PRIMARY_COLORS;

  const [lastCustomColor, setLastCustomColor] = useState(() => {
    const primary = (config.primaryColor || '').toUpperCase();
    const isAlreadyCustom = !!(primary && !presetColors.some(c => c.value.toUpperCase() === primary));
    return isAlreadyCustom ? primary : null;
  });

  const currentPrimary = (config.primaryColor || '').toUpperCase();
  const isCustom = !!(currentPrimary && !presetColors.some(c => c.value.toUpperCase() === currentPrimary));
  const displayColor = isCustom ? currentPrimary : (lastCustomColor || presetColors[0]?.value || '#2000DF');

  const validHex = /^#?[0-9a-fA-F]{6}$/.test(hexInput.trim());
  const previewColor = validHex
    ? (hexInput.trim().startsWith('#') ? hexInput.trim() : `#${hexInput.trim()}`)
    : null;

  const handleHexSubmit = () => {
    const cleaned = hexInput.trim();
    const hex = cleaned.startsWith('#') ? cleaned : `#${cleaned}`;
    if (/^#[0-9a-fA-F]{6}$/.test(hex)) {
      const color = hex.toUpperCase();
      onChange('primaryColor', color);
      setLastCustomColor(color);
      setHexInput('');
    }
  };

  return (
    <div>
      <label className="block text-xs mb-2" style={{ color: ui.textSecondary }}>{control.label}</label>
      {control.showModeToggle && (
        <div className="flex gap-1 mb-3">
          {['single', 'palette'].map((mode) => (
            <button
              key={mode}
              onClick={() => onChange('externalColorMode', mode)}
              className="flex-1 text-xs py-1 px-2 border rounded capitalize"
              style={{
                backgroundColor: config.externalColorMode === mode ? ui.accent : ui.bgSecondary,
                color: config.externalColorMode === mode ? ui.accentFg : ui.textSecondary,
                borderColor: config.externalColorMode === mode ? ui.accent : ui.border,
              }}
            >
              {mode === 'single' ? 'Single' : 'Multi'}
            </button>
          ))}
        </div>
      )}
      <div className="space-y-2">
        {presetColors.map((colorOption) => {
          const hex = colorOption.value;
          const palette = buildPaletteFromPrimary(hex);
          const isSelected = hex.toUpperCase() === currentPrimary;
          return (
            <button
              key={hex}
              onClick={() => onChange('primaryColor', hex)}
              className="flex items-center gap-2 w-full rounded px-2 py-1.5"
              style={{
                backgroundColor: isSelected ? ui.hover : 'transparent',
                border: `1px solid ${isSelected ? ui.border : 'transparent'}`,
              }}
            >
              <div className="w-4 h-4 rounded-sm flex-shrink-0 border" style={{ backgroundColor: hex, borderColor: ui.border }} />
              <div className="flex gap-0.5">
                {palette.map((c, i) => (
                  <div key={i} className="w-3 h-4 rounded-sm" style={{ backgroundColor: c }} />
                ))}
              </div>
            </button>
          );
        })}
        <div className="pt-1" style={{ borderTop: `1px solid ${ui.borderSecondary}` }}>
          <div className="text-[10px] mb-1 font-medium" style={{ color: ui.textSecondary }}>Custom</div>
          {/* Row 1: OS picker swatch + derived palette (visual only) */}
          <button
            onClick={() => lastCustomColor && onChange('primaryColor', lastCustomColor)}
            className="flex items-center gap-2 w-full rounded px-2 py-1.5 mb-1.5"
            style={{
              backgroundColor: isCustom ? ui.hover : 'transparent',
              border: `1px solid ${isCustom ? ui.border : 'transparent'}`,
            }}
          >
            <label
              onClick={(e) => e.stopPropagation()}
              style={{ position: 'relative', display: 'inline-block', flexShrink: 0 }}
            >
              <div className="w-4 h-4 rounded-sm border" style={{ backgroundColor: displayColor, borderColor: ui.border }} />
              <input
                type="color"
                value={displayColor.toLowerCase()}
                onChange={(e) => {
                  const color = e.target.value.toUpperCase();
                  onChange('primaryColor', color);
                  setLastCustomColor(color);
                }}
                style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer', width: '100%', height: '100%' }}
              />
            </label>
            {(isCustom || lastCustomColor) && (
              <div className="flex gap-0.5">
                {buildPaletteFromPrimary(displayColor).map((c, i) => (
                  <div
                    key={i}
                    className="w-3 h-4 rounded-sm"
                    style={{ backgroundColor: c }}
                  />
                ))}
              </div>
            )}
          </button>
          {/* Row 2: hex text input */}
          <div className="flex gap-1 items-center">
            <label style={{ position: 'relative', display: 'inline-block', flexShrink: 0 }}>
              <div
                className="w-7 h-7 rounded-sm border"
                style={{ backgroundColor: previewColor || (isCustom ? currentPrimary : '#ffffff'), borderColor: ui.borderSecondary }}
              />
              <input
                type="color"
                value={(previewColor || (isCustom ? currentPrimary : '#2000df')).toLowerCase()}
                onChange={(e) => setHexInput(e.target.value.toUpperCase())}
                style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer', width: '100%', height: '100%' }}
              />
            </label>
            <input
              type="text"
              value={hexInput}
              onChange={(e) => setHexInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleHexSubmit()}
              placeholder="#2000DF"
              className="flex-1 text-xs border px-1.5 py-1 rounded-sm"
              style={{ borderColor: ui.borderSecondary, color: ui.text, backgroundColor: ui.bg, minWidth: 0 }}
              maxLength={7}
            />
            <button
              onClick={handleHexSubmit}
              className="text-xs border px-2 py-1 rounded-sm ui-hover"
              style={{ borderColor: ui.borderSecondary, color: ui.text }}
            >
              OK
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function ExternalBackgroundControl({ control, config, onChange, ui }) {
  const { resolved: theme, allThemes, themeId, setBulkOverrides, resetOverrides } = useTheme();
  const [hexInput, setHexInput] = useState('');
  const baseTheme = allThemes[themeId] || theme;

  const currentBg = (config.backgroundColor || '#FFFFFF').toUpperCase();
  const isDefault = currentBg === '#FFFFFF';

  const applyBackground = (bgColor) => {
    const normalized = bgColor.toUpperCase();
    onChange('backgroundColor', normalized);

    if (normalized === '#FFFFFF') {
      // Reset to base theme defaults
      resetOverrides();
      return;
    }

    const chartOverrides = deriveThemeFromBackground(normalized, baseTheme);
    const uiOverrides = deriveUiFromBackground(normalized);
    setBulkOverrides({ ...chartOverrides, ui: uiOverrides });
  };

  const handleHexSubmit = () => {
    const cleaned = hexInput.trim();
    const hex = cleaned.startsWith('#') ? cleaned : `#${cleaned}`;
    if (/^#[0-9a-fA-F]{6}$/.test(hex)) {
      applyBackground(hex.toUpperCase());
      setHexInput('');
    }
  };

  const validHex = /^#?[0-9a-fA-F]{6}$/.test(hexInput.trim());
  const previewColor = validHex
    ? (hexInput.trim().startsWith('#') ? hexInput.trim() : `#${hexInput.trim()}`)
    : null;

  return (
    <div>
      <label className="block text-xs mb-2" style={{ color: ui.textSecondary }}>{control.label}</label>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {EXTERNAL_BACKGROUND_COLORS.map((bg) => {
          const isSelected = bg.value.toUpperCase() === currentBg;
          const isDarkSwatch = ['#1E293B', '#1A3A2A', '#000000'].includes(bg.value);
          return (
            <button
              key={bg.value}
              onClick={() => applyBackground(bg.value)}
              className="w-8 h-8 rounded-sm border"
              style={{
                backgroundColor: bg.value,
                borderColor: isSelected ? ui.text : (isDarkSwatch ? '#4B5563' : ui.borderSecondary),
                outline: isSelected ? `2px solid ${ui.text}` : 'none',
                outlineOffset: 1,
              }}
              title={`${bg.label} (${bg.value})`}
            />
          );
        })}
      </div>
      <div className="pt-1" style={{ borderTop: `1px solid ${ui.borderSecondary}` }}>
        <div className="text-[10px] mb-1 font-medium" style={{ color: ui.textSecondary }}>Custom</div>
        <div className="flex gap-1 items-center">
          <label style={{ position: 'relative', display: 'inline-block', flexShrink: 0 }}>
            <div
              className="w-7 h-7 rounded-sm border"
              style={{
                backgroundColor: previewColor || (!isDefault ? currentBg : '#ffffff'),
                borderColor: ui.borderSecondary,
              }}
            />
            <input
              type="color"
              value={(previewColor || (!isDefault ? currentBg : '#ffffff')).toLowerCase()}
              onChange={(e) => setHexInput(e.target.value.toUpperCase())}
              style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer', width: '100%', height: '100%' }}
            />
          </label>
          <input
            type="text"
            value={hexInput}
            onChange={(e) => setHexInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleHexSubmit()}
            placeholder="#1E293B"
            className="flex-1 text-xs border px-1.5 py-1 rounded-sm"
            style={{ borderColor: ui.borderSecondary, color: ui.text, backgroundColor: ui.bg, minWidth: 0 }}
            maxLength={7}
          />
          <button
            onClick={handleHexSubmit}
            className="text-xs border px-2 py-1 rounded-sm ui-hover"
            style={{ borderColor: ui.borderSecondary, color: ui.text }}
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
}
